import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { imgSrc, memberSlug, focusStyle } from "@/lib/api";
import { SubpageShell } from "@/components/layout/SubpageShell";

const EASE = [0.22, 1, 0.36, 1];

export default function MemberProfilePage() {
  const { slug } = useParams();
  const { content, lang, t } = useSite();
  const members = content?.members || [];
  const person = members.find((m) => memberSlug(m) === slug);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(lang === "zh" ? "链接已复制" : "Link copied");
    } catch {
      toast.error(lang === "zh" ? "复制失败" : "Copy failed");
    }
  };

  return (
    <SubpageShell titleZh={person ? t(person, "name") : "我们的伙伴"} titleEn={person ? person.name_en : "Our Partners"}>
      <div data-testid="member-profile-page" className="relative max-w-3xl mx-auto px-6 lg:px-12 pt-20 pb-28">
        {!person ? (
          <div data-testid="member-not-found" className="text-center py-24">
            <p className="font-display font-extrabold text-2xl text-slate-900">
              {lang === "zh" ? "没有找到这位伙伴" : "Partner not found"}
            </p>
            <a href="/members" className="mt-6 inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 text-sm font-bold text-white hover:bg-orange-600 transition-colors duration-300">
              <ArrowLeft size={15} />
              {lang === "zh" ? "查看全部伙伴" : "View all partners"}
            </a>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <div className="relative rounded-[2rem] overflow-hidden bg-sky-100 aspect-[4/3] shadow-[0_24px_60px_rgb(56,189,248,0.18)]">
              {imgSrc(person.avatar) && (
                <img src={imgSrc(person.avatar)} alt={t(person, "name")} style={focusStyle(person)} className="absolute inset-0 w-full h-full object-cover" />
              )}
              <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-slate-900/60 to-transparent" />
              <div className="absolute bottom-6 left-8">
                <h1 data-testid="member-profile-name" className="font-display font-black tracking-tight text-3xl sm:text-4xl text-white">
                  {t(person, "name")}
                </h1>
                <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-orange-600">
                  <BadgeCheck size={13} />
                  {t(person, "role")}
                </p>
              </div>
            </div>

            {(t(person, "bio") || "").split("\n").filter(Boolean).map((para, i) => (
              <p key={i} data-testid={`member-profile-bio-${i}`} className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600">
                {para}
              </p>
            ))}

            <div className="mt-10 flex flex-wrap gap-3">
              {person.join_url && (
                <a data-testid="member-profile-join" href={person.join_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-full bg-orange-500 px-7 py-3 text-sm font-bold text-white hover:bg-orange-600 hover:-translate-y-0.5 transition-[background-color,transform] duration-300">
                  {lang === "zh" ? "我想加入" : "Join Us"}
                </a>
              )}
              {person.policy_url && (
                <a data-testid="member-profile-policy" href={person.policy_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-full border-2 border-sky-400 px-7 py-[10px] text-sm font-bold text-sky-500 hover:bg-sky-400 hover:text-white hover:-translate-y-0.5 transition-[background-color,color,transform] duration-300">
                  {lang === "zh" ? "了解保单" : "View Plans"}
                </a>
              )}
              <button data-testid="member-profile-copy" onClick={copyLink}
                className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-7 py-3 text-sm font-bold text-white hover:bg-slate-700 hover:-translate-y-0.5 transition-[background-color,transform] duration-300">
                <Link2 size={15} />
                {lang === "zh" ? "复制专属链接" : "Copy Link"}
              </button>
            </div>

            <a data-testid="member-profile-back" href="/members"
              className="mt-12 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-sky-500 transition-colors duration-300">
              <ArrowLeft size={15} />
              {lang === "zh" ? "返回我们的伙伴" : "Back to Our Partners"}
            </a>
          </motion.div>
        )}
      </div>
    </SubpageShell>
  );
}
